export type SeasonalPeriod = {
  date_from: string;
  date_to: string;
  season_type?: string;
};

const parseYmdParts = (value: string): [number, number, number] | null => {
  const parts = value.trim().split('-');
  if (parts.length !== 3 || !parts[0] || !parts[1] || !parts[2]) return null;
  const y = Number.parseInt(parts[0], 10);
  const m = Number.parseInt(parts[1], 10);
  const d = Number.parseInt(parts[2], 10);
  if (Number.isNaN(y) || Number.isNaN(m) || Number.isNaN(d)) return null;
  const date = new Date(y, m - 1, d);
  if (date.getFullYear() !== y || date.getMonth() !== m - 1 || date.getDate() !== d) return null;
  return [y, m, d];
};

const calendarDayStart = (date: Date): Date =>
  new Date(date.getFullYear(), date.getMonth(), date.getDate());

const compareCalendarDays = (a: Date, b: Date): number =>
  calendarDayStart(a).getTime() - calendarDayStart(b).getTime();

export const isDayWithinInclusiveRange = (day: Date, fromYmd: string, toYmd: string): boolean => {
  const fromParts = parseYmdParts(fromYmd);
  const toParts = parseYmdParts(toYmd);
  if (!fromParts || !toParts) return false;
  const from = new Date(fromParts[0], fromParts[1] - 1, fromParts[2]);
  const to = new Date(toParts[0], toParts[1] - 1, toParts[2]);
  const t = compareCalendarDays(day, from);
  const u = compareCalendarDays(day, to);
  return t >= 0 && u <= 0;
};

export const isDayInAnySeasonalPeriod = (day: Date, periods: SeasonalPeriod[]): boolean =>
  periods.some((p) => isDayWithinInclusiveRange(day, p.date_from, p.date_to));

export const addCalendarDays = (start: Date, daysToAdd: number): Date => {
  const d = new Date(start.getFullYear(), start.getMonth(), start.getDate());
  d.setDate(d.getDate() + daysToAdd);
  return d;
};

const formatYmd = (d: Date): string => {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${String(y)}-${m}-${day}`;
};

/**
 * Joins overlapping or consecutive calendar ranges (e.g. High ends 29 Mar, Low starts 30 Mar → one season).
 */
export function mergeAdjacentSeasonalPeriods(periods: SeasonalPeriod[]): SeasonalPeriod[] {
  type Interval = { start: Date; end: Date };
  const list: Interval[] = [];
  for (const p of periods) {
    const fs = parseYmdParts(p.date_from);
    const ts = parseYmdParts(p.date_to);
    if (!fs || !ts) continue;
    list.push({
      start: new Date(fs[0], fs[1] - 1, fs[2]),
      end: new Date(ts[0], ts[1] - 1, ts[2]),
    });
  }
  if (!list.length) return [];

  list.sort((a, b) => compareCalendarDays(a.start, b.start));

  const out: Interval[] = [];
  let cur = list[0]!;
  for (let i = 1; i < list.length; i++) {
    const next = list[i]!;
    const dayAfterCurEnd = addCalendarDays(cur.end, 1);
    if (compareCalendarDays(next.start, dayAfterCurEnd) <= 0) {
      if (compareCalendarDays(next.end, cur.end) > 0) {
        cur.end = next.end;
      }
    } else {
      out.push(cur);
      cur = next;
    }
  }
  out.push(cur);

  return out.map((iv) => ({
    date_from: formatYmd(iv.start),
    date_to: formatYmd(iv.end),
  }));
}

export const parseTripStartingDate = (ymd: string): Date | null => {
  const parts = parseYmdParts(ymd);
  if (!parts) return null;
  return new Date(parts[0], parts[1] - 1, parts[2]);
};

const MONTH_SHORT_EN = [
  'Jan',
  'Feb',
  'Mar',
  'Apr',
  'May',
  'Jun',
  'Jul',
  'Aug',
  'Sep',
  'Oct',
  'Nov',
  'Dec',
] as const;

const formatDayMonCommaYear = (d: Date): string => {
  const day = d.getDate();
  const mon = MONTH_SHORT_EN[d.getMonth()] ?? '';
  const y = d.getFullYear();
  return `${String(day)} ${mon}, ${String(y)}`;
};

const intervalOverlapsCalendarMonth = (
  intervalStart: Date,
  intervalEnd: Date,
  monthStart: Date,
  monthEnd: Date
): boolean =>
  compareCalendarDays(intervalStart, monthEnd) <= 0 &&
  compareCalendarDays(intervalEnd, monthStart) >= 0;

/**
 * Footer for the merged window that overlaps the visible month (handles disjoint seasons with gaps).
 * Gap months (no overlap) return null.
 */
export function getSeasonEndFooterTextForVisibleMonth(
  periods: SeasonalPeriod[],
  viewYear: number,
  viewMonthIndex: number
): string | null {
  const merged = mergeAdjacentSeasonalPeriods(periods);
  if (!merged.length) return null;

  const monthStart = new Date(viewYear, viewMonthIndex, 1);
  const monthEnd = new Date(viewYear, viewMonthIndex + 1, 0);

  for (const p of merged) {
    const fs = parseYmdParts(p.date_from);
    const ts = parseYmdParts(p.date_to);
    if (!fs || !ts) continue;
    const iStart = new Date(fs[0], fs[1] - 1, fs[2]);
    const iEnd = new Date(ts[0], ts[1] - 1, ts[2]);
    if (!intervalOverlapsCalendarMonth(iStart, iEnd, monthStart, monthEnd)) continue;
    return `Season ends on ${formatDayMonCommaYear(iEnd)}`;
  }

  return null;
}
